import { getHubSpotSessionToken } from '@/lib/hubspotSession';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import api from '@/lib/api';
import type { HubSpotCompany, HubSpotContact, HubSpotDeal } from '@/types';

export interface HubSpotQuoteData {
  deal?: HubSpotDeal;
  company?: HubSpotCompany | null;
  contacts?: HubSpotContact[];
}

export type HubSpotQuoteDataError = 'deal' | 'company' | 'contacts';

const isTruthy = (value: unknown) =>
  value === true || value === 'true' || value === '1' || value === 1;

/** Terug uit HubSpot vuren `visibilitychange` en `focus` vaak samen: één keer ophalen is genoeg. */
const REFRESH_ON_RETURN_INTERVAL_MS = 5_000;

export function selectPrimaryContact(contacts?: HubSpotContact[]): HubSpotContact | undefined {
  if (!contacts || contacts.length === 0) return undefined;
  const primary = contacts.find((contact) =>
    isTruthy(contact?.properties?.hs_is_primary_contact) ||
    isTruthy(contact?.properties?.primary_contact) ||
    isTruthy(contact?.properties?.is_primary) ||
    isTruthy(contact?.properties?.primary)
  );
  return primary || contacts.find((contact) => contact?.properties?.firstname || contact?.properties?.lastname) || contacts[0];
}

/**
 * Haalt deal/bedrijf/contacten parallel op uit HubSpot voor de editor.
 * Toont één waarschuwingstoast bij (deels) mislukte fetches.
 *
 * Wie de deal in HubSpot aanpast (contactpersonen koppelen, dealnaam wijzigen) en terugkomt
 * in dit tabblad, krijgt de nieuwe gegevens zonder herladen. Zo'n verversing laat de vorige
 * gegevens staan tot de nieuwe binnen zijn; wat dan mislukt, blijft zoals het was.
 */
export function useHubSpotQuoteData(portalId: string | null | undefined, dealId: string | null | undefined) {
  const [hubspotData, setHubspotData] = useState<HubSpotQuoteData>({});
  const [hubspotDataLoaded, setHubspotDataLoaded] = useState(!portalId || !dealId);
  const [hubspotDataErrors, setHubspotDataErrors] = useState<HubSpotQuoteDataError[]>([]);
  const [hubspotDataRefreshing, setHubspotDataRefreshing] = useState(false);
  const hubspotErrorShown = useRef(false);
  /** Alleen het antwoord op de laatste aanvraag telt; oudere antwoorden (of van een vorige deal) vervallen. */
  const latestRequest = useRef(0);
  const lastFetchStartedAt = useRef(0);

  /** `undefined`: er is intussen een nieuwere aanvraag gestart, dit antwoord telt niet meer. */
  const fetchHubSpotData = useCallback(async (refresh: boolean): Promise<boolean | undefined> => {
    if (!portalId || !dealId) return undefined;
    const request = ++latestRequest.current;
    lastFetchStartedAt.current = Date.now();
    if (refresh) setHubspotDataRefreshing(true);

    try {
      const [dealResult, companyResult, contactsResult] = await Promise.allSettled([
        api.getDeal(dealId, portalId),
        api.getDealCompany(dealId, portalId),
        api.getDealContacts(dealId, portalId),
      ]);

      if (request !== latestRequest.current) return undefined;

      const failed = (['deal', 'company', 'contacts'] as const).filter((key, index) =>
        [dealResult, companyResult, contactsResult][index].status === 'rejected'
      );
      setHubspotData((prev) => ({
        deal: dealResult.status === 'fulfilled' ? dealResult.value : refresh ? prev.deal : undefined,
        company: companyResult.status === 'fulfilled' ? companyResult.value : refresh ? prev.company : undefined,
        contacts: contactsResult.status === 'fulfilled' ? contactsResult.value : refresh ? prev.contacts : undefined,
      }));
      setHubspotDataErrors((prev) => failed.filter((key) => !refresh || prev.includes(key)));
      setHubspotDataLoaded(true);
      setHubspotDataRefreshing(false);

      if (failed.length > 0 && !refresh && !hubspotErrorShown.current) {
        hubspotErrorShown.current = true;
        const labels = { deal: 'deal', company: 'bedrijf', contacts: 'contacten' };
        const firstRejected = [dealResult, companyResult, contactsResult].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
        const err = firstRejected?.reason as { response?: { data?: { message?: string } }; message?: string } | undefined;
        const errMsg = err?.response?.data?.message || err?.message || '';
        if (import.meta.env.DEV) {
          console.warn('[HubSpot] Ophalen mislukt:', { deal: dealResult, company: companyResult, contacts: contactsResult });
        }
        const hint = !getHubSpotSessionToken()
          ? ' Verbind eerst met HubSpot via de startpagina.'
          : /credentials|No credentials|access.?token/i.test(errMsg)
            ? ' Herverbind het HubSpot-portaal via de app-instellingen.'
            : '';
        toast.warning(`HubSpot data ophalen mislukt (${failed.map((key) => labels[key]).join(', ')}).${hint}`);
      }
      return failed.length === 0;
    } catch {
      if (request !== latestRequest.current) return undefined;
      if (!refresh) {
        setHubspotData({});
        setHubspotDataErrors(['deal', 'company', 'contacts']);
      }
      setHubspotDataLoaded(true);
      setHubspotDataRefreshing(false);
      return false;
    }
  }, [portalId, dealId]);

  useEffect(() => {
    hubspotErrorShown.current = false;
    setHubspotData({});
    setHubspotDataErrors([]);
    setHubspotDataRefreshing(false);
    if (!portalId || !dealId) {
      latestRequest.current++;
      setHubspotDataLoaded(true);
      return;
    }
    setHubspotDataLoaded(false);
    void fetchHubSpotData(false);

    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchStartedAt.current < REFRESH_ON_RETURN_INTERVAL_MS) return;
      void fetchHubSpotData(true);
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('focus', refreshOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('focus', refreshOnReturn);
    };
  }, [portalId, dealId, fetchHubSpotData]);

  const refreshHubSpotData = useCallback(async () => {
    if (await fetchHubSpotData(true) === false) {
      toast.warning('HubSpot-gegevens vernieuwen mislukt. De vorige gegevens blijven staan.');
    }
  }, [fetchHubSpotData]);

  const primaryContact = useMemo(() => selectPrimaryContact(hubspotData.contacts), [hubspotData.contacts]);

  return {
    hubspotData,
    hubspotDataLoaded,
    hubspotDataErrors,
    hubspotDataRefreshing,
    refreshHubSpotData,
    primaryContact,
  };
}
